import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import * as BioentityDAO from 'bioentities/BioentityDAO';
import {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {
  longDisplayTextForSchemaValidationErrorLabel,
  SchemaValidationErrorLabel,
  SchemaValidationStatus,
} from 'schemas/SchemaValidationConstants';
import {FilterKey, Operator, SortKey} from 'search/SearchConstants';
import {
  TagSchemaValidationSettingsQuery,
  TagSchemaValidationSettingsQueryVariables,
} from 'settings/registry/__generated/TagSchemaValidationSettingsQuery';
import {
  gqlTagSchemaWithValidationSummariesToREST,
  TagSchemaWithValidationSummaries,
  ValidationErrorLabelSummary,
} from 'settings/registry/validation/ValidationSummaries';
import {tagSchemaToAllOligoAwareFolderItemTypes} from 'tag-schema/util/TagSchemaUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

export const TAG_SCHEMA_VALIDATION_SETTINGS_QUERY_POLL_INTERVAL_MS = 15_000;

export const TAG_SCHEMA_VALIDATION_SETTINGS_QUERY = gql`
  query TagSchemaValidationSettingsQuery($tagSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ...TagSchemaGraphQL
      ...TagSchemaValidationStatusSummaryGraphQL
      ...TagSchemaValidationErrorLabelSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('TagSchemaValidationErrorLabelSummary').fragment}
  ${getFragmentWrapper('TagSchemaValidationStatusSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// Keep in sync with ERROR_LABEL_CONSTANT_TO_TEXT in
// benchling/sightglass2/filters/schemas/schema_validation_error_label_filter.py
export const ERROR_LABELS = [
  SchemaValidationErrorLabel.CONSTRAINT_VIOLATION,
  SchemaValidationErrorLabel.INVALID_LINK,
  SchemaValidationErrorLabel.INVALID_VALUE,
  SchemaValidationErrorLabel.MISSING_REQUIRED,
  SchemaValidationErrorLabel.INVALID_MULTIPLE_LINK,
  SchemaValidationErrorLabel.OTHER,
];

export function getDefaultRowSelection(selected: boolean): RowSelection {
  return objectFromEntries(ERROR_LABELS.map((label) => [label, selected]));
}

export function getValidationSearchUrl(
  tagSchema: TagSchema,
  status: SchemaValidationStatus,
  errorLabel?: SchemaValidationErrorLabel
): string {
  return getNavigationURL(
    '/search',
    {
      offset: 0,
      limit: 25,
      sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
      filters: [
        {
          key: FilterKey.SCHEMA,
          operator: Operator.IS_ONE_OF,
          value: [tagSchema.id],
        },
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: tagSchemaToAllOligoAwareFolderItemTypes(tagSchema),
        },
        {
          key: FilterKey.SCHEMA_VALIDATION_STATUS,
          operator: Operator.IS_ONE_OF,
          value: [status],
        },
        ...(errorLabel
          ? [
              {
                key: FilterKey.SCHEMA_VALIDATION_ERROR_LABEL as const,
                operator: Operator.IS_ONE_OF,
                value: [errorLabel],
              },
            ]
          : []),
      ],
      query: '',
    },
    [null, null, null, ...(errorLabel ? [null] : [])]
  );
}

interface ValidationErrorLabelSummaryRowProps {
  tagSchema: TagSchema;
  errorLabelSummary: ValidationErrorLabelSummary;
  errorLabel: SchemaValidationErrorLabel;
  isSelected: boolean;
  onChangeSelected: (selected: boolean) => void;
}

function ValidationErrorLabelSummaryRow(props: ValidationErrorLabelSummaryRowProps): JSX.Element {
  let count = 0;
  switch (props.errorLabel) {
    case SchemaValidationErrorLabel.CONSTRAINT_VIOLATION:
      count = props.errorLabelSummary.numConstraintViolation;
      break;
    case SchemaValidationErrorLabel.INVALID_LINK:
      count = props.errorLabelSummary.numInvalidLink;
      break;
    case SchemaValidationErrorLabel.INVALID_MULTIPLE_LINK:
      count = props.errorLabelSummary.numInvalidMultipleLink;
      break;
    case SchemaValidationErrorLabel.INVALID_VALUE:
      count = props.errorLabelSummary.numInvalidValue;
      break;
    case SchemaValidationErrorLabel.MISSING_REQUIRED:
      count = props.errorLabelSummary.numMissingRequired;
      break;
    case SchemaValidationErrorLabel.OTHER:
      count = props.errorLabelSummary.numOther;
      break;
    default:
      assertAndContinue(false, `Error label not handled: ${props.errorLabel} (using 0 as fallback value)`);
  }
  return (
    <div className="simpleDivTable-row">
      <div className={`simpleDivTable-cell ${css(styles.checkboxCell)}`}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={(ev) => {
            props.onChangeSelected(ev.target.checked);
          }}
        />
      </div>
      <div className="simpleDivTable-cell">
        {longDisplayTextForSchemaValidationErrorLabel(props.errorLabel)}
      </div>
      <div className="simpleDivTable-cell">
        {count ? (
          <a
            href={getValidationSearchUrl(props.tagSchema, SchemaValidationStatus.FAILED, props.errorLabel)}
            className={css(styles.failureTypeCountCell)}
          >
            {count}
          </a>
        ) : null}
      </div>
    </div>
  );
}

interface ValidationStatusSummaryCellProps {
  tagSchema: TagSchemaWithValidationSummaries;
  status: SchemaValidationStatus.OVERRIDDEN | SchemaValidationStatus.FAILED;
}

function ValidationStatusSummaryCell(props: ValidationStatusSummaryCellProps): JSX.Element {
  if (!props.tagSchema.validationStatusSummary) {
    return <td />;
  }
  const summaryValue =
    props.status === SchemaValidationStatus.OVERRIDDEN
      ? props.tagSchema.validationStatusSummary.numOverridden
      : props.tagSchema.validationStatusSummary.numFailed;
  return (
    <td>
      {summaryValue ? (
        <a
          className={css(styles.summaryTableLink)}
          href={getValidationSearchUrl(props.tagSchema, props.status)}
        >
          {summaryValue}
        </a>
      ) : (
        <span className={css(styles.summaryTableLink)}>{summaryValue}</span>
      )}
    </td>
  );
}

function FailureCountPopoverContent(): JSX.Element {
  return (
    <div className="tagSchemaSettings-popover">
      <p>
        This column contains a count of entities for this schema that failed validation, broken out by each
        failure type. Entities whose validation failure has been overridden are not included in these counts.
      </p>
      <p>
        Each value in this column links to a search for entities for this schema that failed validation with a
        failure type that corresponds to its row.
      </p>
      <p>
        <strong>Note:</strong> A single entity may fail validation for more than one reason, causing it to be
        counted more than once in this column.
      </p>
    </div>
  );
}

export interface Props {
  tagSchemaId: string;
}

export interface RowSelection {
  [errorLabel: string]: boolean;
}
export interface QueryingProps extends Props {
  tagSchema: TagSchemaWithValidationSummaries;
  onOverrideValidationChecks: () => Promise<void>;
  onChangeSelection: (selection: RowSelection) => void;
  isSelectedByErrorLabel: RowSelection;
}

export function TagSchemaValidationSettings(props: Props & QueryingProps): JSX.Element | null {
  const [isOverriding, setIsOverriding] = useState<boolean>(false);
  if (
    !(
      props.tagSchema &&
      props.tagSchema.validationStatusSummary &&
      props.tagSchema.validationErrorLabelSummary
    )
  ) {
    return null;
  }

  return (
    <div className="settingsPage-contentWrapper">
      <table className={css(styles.summaryTable)}>
        <thead>
          <tr>
            <th>Entities with Failures</th>
            <th>Entities with Overridden Failures</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <ValidationStatusSummaryCell tagSchema={props.tagSchema} status={SchemaValidationStatus.FAILED} />
            <ValidationStatusSummaryCell
              tagSchema={props.tagSchema}
              status={SchemaValidationStatus.OVERRIDDEN}
            />
          </tr>
        </tbody>
      </table>

      <Button
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        className={css(styles.overrideButton)}
        text="Override Validation Checks"
        disabled={isOverriding}
        onClick={async () => {
          setIsOverriding(true);
          await props.onOverrideValidationChecks();
          setIsOverriding(false);
        }}
        size={ButtonSize.SMALL}
      />

      <div className={`simpleDivTable ${css(styles.failureTypeTable)}`}>
        <div className="simpleDivTable-header">
          <div className={`simpleDivTable-headerCell ${css(styles.checkboxCell)}`}>
            <input
              type="checkbox"
              checked={Object.entries(props.isSelectedByErrorLabel).every((entry) => entry[1])}
              onChange={(ev) => {
                props.onChangeSelection(getDefaultRowSelection(ev.target.checked));
              }}
            />
          </div>
          <div className="simpleDivTable-headerCell">Failure Type</div>
          <div className="simpleDivTable-headerCell">
            <span className={css(styles.failureTypeCountCell)}>Count</span>
            <HelpButton
              title="Validation Failure Type / Count"
              renderContent={() => <FailureCountPopoverContent />}
              iconClassName="tagSchemaSettings-helpIcon u-tiny-spacing-left"
            />
          </div>
        </div>
        {ERROR_LABELS.map((errorLabel) => (
          <ValidationErrorLabelSummaryRow
            key={errorLabel}
            tagSchema={props.tagSchema}
            errorLabel={errorLabel}
            errorLabelSummary={props.tagSchema.validationErrorLabelSummary!}
            isSelected={props.isSelectedByErrorLabel[errorLabel]}
            onChangeSelected={(isSelected: boolean) => {
              props.onChangeSelection({...props.isSelectedByErrorLabel, [errorLabel]: isSelected});
            }}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  summaryTable: {
    marginBottom: 10,
  },
  summaryTableLink: {
    display: 'inline-block',
    width: 250,
    padding: '5px 0',
  },
  overrideButton: {
    marginBottom: 10,
    float: 'right',
  },
  failureTypeTable: {
    clear: 'both',
  },
  checkboxCell: {
    maxWidth: 40,
  },
  failureTypeCountCell: {
    display: 'inline-block',
    width: '100%',
    maxWidth: 50,
    textAlign: 'right',
  },
});

export default function QueryingTagSchemaValidationSettings(props: Props): JSX.Element {
  const [isSelectedByErrorLabel, setIsSelectedByErrorLabel] = useState(getDefaultRowSelection(false));
  const validationQueryResult = useBenchlingQuery<
    TagSchemaValidationSettingsQuery,
    TagSchemaValidationSettingsQueryVariables
  >(TAG_SCHEMA_VALIDATION_SETTINGS_QUERY, {
    variables: {tagSchemaId: props.tagSchemaId},
    fetchPolicy: 'cache-and-network',
    pollInterval: TAG_SCHEMA_VALIDATION_SETTINGS_QUERY_POLL_INTERVAL_MS,
    renderCachedDataWhenRefetching: true,
  });
  if (validationQueryResult.type !== QueryResultType.SUCCESS) {
    return <HorizontalThrobber />;
  }
  assert(!isInaccessible(validationQueryResult.data.tagSchema), 'Expected tag schema to be accessible.');

  return (
    <TagSchemaValidationSettings
      {...props}
      tagSchema={gqlTagSchemaWithValidationSummariesToREST(validationQueryResult.data.tagSchema)}
      onOverrideValidationChecks={async () => {
        const selectedErrorLabels = Object.entries(isSelectedByErrorLabel)
          .filter((entry) => entry[1])
          .map((entry) => entry[0]);
        if (selectedErrorLabels.length) {
          await BioentityDAO.bulkOverrideValidationStatus(props.tagSchemaId, selectedErrorLabels);
          setIsSelectedByErrorLabel(getDefaultRowSelection(false));
        }
        validationQueryResult.refetch();
      }}
      onChangeSelection={(selection: RowSelection) => {
        setIsSelectedByErrorLabel(selection);
      }}
      isSelectedByErrorLabel={isSelectedByErrorLabel}
    />
  );
}
